import { type JSX, useCallback } from 'react';

import { AdfoxParallaxBannerLoadable } from '@/packages/adfox/AdfoxParallaxBanner';
import { AdfoxTGBBannerLoadable } from '@/packages/adfox/AdfoxTGBBanner/AdfoxTGBBannerLoadable';
import { type AdfoxBannersConfig, type AdfoxContextData } from '@/packages/adfox/types/AdfoxContextData';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useReduxSelector } from '@/packages/redux-state/store';
import { isCommercialSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/isCommercialSelector';
import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { isLastListItem } from '@/packages/utils/array';

import { type IOfferForDesktopEnrichedV3Schema } from '@/repositories/generated/shared/search-offers/entities/offer_for_desktop/OfferForDesktopEnrichedV3Schema';

import { fetchGetNewbuildingsInfoForTgbSafe } from '../../apiHelpers/fetchGetNewbuildingsInfoForTgbSafe';
import { useListingAppContext } from '../../context/ListingAppContext';
import { type TCardApiContext } from '../../types/TCardApiContext';
import { type TCardContextData } from '../../types/TCardContextData';

import { ResidentialCard as Card } from './ResidentialCard';
import { AdfoxParallaxBannerWrapper, AdfoxTgbBannerWrapper, CardWrapper } from './components/desktop/CardsLayout';

type CardsProps = {
  adfox: AdfoxContextData['serverData'];
  adfoxConfig: AdfoxBannersConfig;
  dealType: 'sale' | 'rent';
  offers: IOfferForDesktopEnrichedV3Schema[];
  initCardApi: (cardData: TCardContextData) => TCardApiContext;
  renderAdvertisment?: (props: { index: number; previousOfferId: number }) => JSX.Element;
};

// const CommercialCard = loadable(() => import('./CommercialCard'), {
//   resolveComponent: module => module.CommercialCard,
//   ssr: true,
// });

export const Cards = memoWithDisplayName<CardsProps>(props => {
  const { dealType, offers, adfox, adfoxConfig, initCardApi } = props;
  const isNarrow = useListingLayoutIsNarrowSelector();
  const isCommercial = useReduxSelector(isCommercialSelector);

  // TODO Пока не используется
  // const cardType = useCardType();

  // const Card = useMemo(() => {
  //   if (cardType === 'commercial') {
  //     return CommercialCard;
  //   }

  //   return ResidentialCard;
  // }, [cardType]);

  const ctx = useListingAppContext();

  const enrichTgbNativedescGeneralBanner = useCallback(
    async (newbuildingId: number) =>
      fetchGetNewbuildingsInfoForTgbSafe(ctx, { newbuildingIds: [newbuildingId] }).then(res =>
        res.map(data => data.newbuildings[0]).unwrapOr(null),
      ),
    [ctx],
  );

  return offers.reduce((accumulator, offer, index, offersArr) => {
    accumulator.push(
      <CardWrapper key={offer.id} narrow={isNarrow} responsive={!isCommercial}>
        <Card dealType={dealType} index={index} initCardApi={initCardApi} offer={offer} />
      </CardWrapper>,
    );

    if (isLastListItem(offersArr, index)) {
      return accumulator;
    }

    /**
     * @description каждое 10 объявление: 10, 20, 30, ...
     */
    if ((index + 1) % 10 === 0) {
      accumulator.push(
        <AdfoxParallaxBannerWrapper key={`adfox_parallax_banner_next_to_${offer.id}`}>
          <AdfoxParallaxBannerLoadable adfox={adfox} config={adfoxConfig} isNarrow={isNarrow} />
        </AdfoxParallaxBannerWrapper>,
      );
      /**
       * @description каждое 10 объявление если начинать с 5: 5, 15, 25, ...
       */
    } else if ((index + 1) % 10 === 5) {
      accumulator.push(
        <AdfoxTgbBannerWrapper key={`adfox_tgb_banner_next_to_${offer.id}`}>
          <AdfoxTGBBannerLoadable
            adfox={adfox}
            config={adfoxConfig}
            enrichTgbNativedescGeneralBanner={enrichTgbNativedescGeneralBanner}
            isNarrow={isNarrow}
          />
        </AdfoxTgbBannerWrapper>,
      );
    }

    return accumulator;
  }, Array.of<JSX.Element>());
}, 'Cards');
